import { zodResolver } from '@hookform/resolvers/zod';
import { useForm, useWatch } from 'react-hook-form';
import { z } from 'zod';

import { EntitySelect } from '@/components/EntitySelect';
import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { FormSection } from '@/components/ui/form-section';
import { InfoHint } from '@/components/ui/info-hint';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type { ApiError } from '@/core/api';
import { resolveCompanyLabel, searchCompanies } from '@/features/crm/companies/select';
import { resolveContactLabel, searchContacts } from '@/features/crm/contacts/select';
import { resolveLeadLabel, searchLeads } from '@/features/crm/leads/select';
import { canEditCommission, canSeeMoney } from '@/lib/money';
import { MemberSelect } from '@/features/members';
import { CRM_OWNER_ROLES } from '@/features/crm/constants';
import { useSession } from '@/stores/session';
import {
  COMMISSION_BASES,
  CURRENCIES,
  DEAL_STAGES,
  type LostReason,
} from '../constants';
import type { DealInput } from '../types';
import { LostReasonSelect } from './LostReasonSelect';

const schema = z.object({
  name: z.string().trim().min(1, 'Name is required').max(255),
  company: z.string().min(1, 'Company is required'),
  primary_contact: z.string(),
  lead: z.string(),
  owner: z.string(),
  stage: z.enum(['qualified', 'proposal', 'negotiation', 'won', 'lost']),
  amount: z.string().refine((v) => v === '' || !Number.isNaN(Number(v)), 'Enter a valid amount'),
  currency: z.enum(['USD', 'EUR', 'GBP', 'AUD']),
  probability: z
    .string()
    .refine((v) => v !== '' && Number(v) >= 0 && Number(v) <= 100, 'Enter 0–100'),
  commission_pct: z
    .string()
    .refine((v) => v === '' || (Number(v) >= 0 && Number(v) <= 100), 'Enter 0–100'),
  commission_basis: z.enum(['none', 'first_client_invoice', 'all_paid_invoices']),
  expected_close_date: z.string(),
  lost_reason: z.string(),
  lost_note: z.string(),
})
  // A Lost deal says why (ADR-108) — the server refuses it otherwise.
  .refine((v) => v.stage !== 'lost' || v.lost_reason !== '', {
    path: ['lost_reason'],
    message: 'Pick why the deal was lost',
  });

type FormValues = z.infer<typeof schema>;

interface DealFormProps {
  defaultValues?: Partial<FormValues>;
  submitLabel: string;
  onSubmit: (values: DealInput) => Promise<unknown>;
  onCancel: () => void;
}

const FIELD_KEYS = [
  'name',
  'company',
  'primary_contact',
  'lead',
  'owner',
  'stage',
  'amount',
  'currency',
  'probability',
  'commission_pct',
  'commission_basis',
  'expected_close_date',
  'lost_reason',
  'lost_note',
] as const;

export function DealForm({ defaultValues, submitLabel, onSubmit, onCancel }: DealFormProps) {
  // Money fields are sensitive (C7/F3) — gate editing by role to match the
  // server-side masking. Non-money roles see disabled inputs with a hint.
  const role = useSession((s) => s.role);
  const moneyEditable = canSeeMoney(role);
  // Salesperson can see commission but can't set their own rate — admin sets it.
  const commissionEditable = canEditCommission(role);
  // A new deal opens on the org's default basis (Settings → Organization), so an
  // org that pays no commission doesn't have to clear the field every time.
  const organizations = useSession((s) => s.organizations);
  const activeOrgSlug = useSession((s) => s.activeOrgSlug);
  const orgDefaultBasis =
    organizations.find((o) => o.slug === activeOrgSlug)?.default_commission_basis ??
    'first_client_invoice';

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      name: '',
      company: '',
      primary_contact: '',
      lead: '',
      owner: '',
      stage: 'qualified',
      amount: '',
      currency: 'USD',
      probability: '0',
      commission_pct: '',
      commission_basis: orgDefaultBasis,
      expected_close_date: '',
      lost_reason: '',
      lost_note: '',
      ...defaultValues,
    },
  });

  // "No commission" is the switch for whether this deal pays at all — the rate
  // is meaningless under it, so it's cleared and locked (mirrors the server).
  const noCommission =
    useWatch({ control: form.control, name: 'commission_basis' }) === 'none';
  const isLost = useWatch({ control: form.control, name: 'stage' }) === 'lost';

  async function handleSubmit(values: FormValues) {
    const input: DealInput = {
      name: values.name,
      company: Number(values.company),
      primary_contact: values.primary_contact ? Number(values.primary_contact) : null,
      lead: values.lead ? Number(values.lead) : null,
      owner: values.owner ? Number(values.owner) : null,
      stage: values.stage,
      currency: values.currency,
      amount: values.amount.trim() === '' ? null : values.amount.trim(),
      probability: values.probability.trim() === '' ? '0' : values.probability.trim(),
      commission_pct:
        values.commission_basis === 'none' || values.commission_pct.trim() === ''
          ? null
          : values.commission_pct.trim(),
      commission_basis: values.commission_basis,
      expected_close_date: values.expected_close_date || null,
      // Only a Lost deal carries a reason; the server clears it otherwise.
      lost_reason: values.stage === 'lost' ? (values.lost_reason as LostReason) : '',
      lost_note: values.stage === 'lost' ? values.lost_note.trim() : '',
    };
    try {
      await onSubmit(input);
    } catch (e) {
      const err = e as ApiError;
      if (err?.fieldErrors) {
        for (const [field, messages] of Object.entries(err.fieldErrors)) {
          const key = FIELD_KEYS.find((k) => k === field);
          form.setError(key ?? 'root', { message: messages.join(' ') });
        }
      }
    }
  }

  const submitting = form.formState.isSubmitting;

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className="max-w-2xl space-y-6">
        <FormSection variant="card" title="Deal" description="What's the opportunity?">
          <FormField
            control={form.control}
            name="name"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Name</FormLabel>
                <FormControl>
                  <Input placeholder="Website redesign — Acme" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </FormSection>

        <FormSection variant="card" title="Money" description="What's it worth?">
          <div className="grid gap-4 sm:grid-cols-2">
            <FormField
              control={form.control}
              name="amount"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="flex items-center gap-1.5">
                    Estimated value
                    {!moneyEditable ? (
                      <InfoHint text="Money fields are hidden for your role." />
                    ) : null}
                  </FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      min={0}
                      step="0.01"
                      placeholder={moneyEditable ? '0.00' : 'Hidden for your role'}
                      disabled={!moneyEditable}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="currency"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Currency</FormLabel>
                  <Select
                    value={field.value}
                    onValueChange={field.onChange}
                    disabled={!moneyEditable}
                  >
                    <FormControl>
                      <SelectTrigger className="w-full">
                        <SelectValue />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {CURRENCIES.map((c) => (
                        <SelectItem key={c} value={c}>
                          {c}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <FormField
              control={form.control}
              name="commission_pct"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="flex items-center gap-1.5">
                    Commission %
                    <InfoHint
                      text={
                        noCommission
                          ? 'This deal pays no commission — pick a basis to set a rate.'
                          : commissionEditable
                            ? 'The salesperson earns this % of the qualifying revenue on this deal. Prefilled from the owner’s default rate; you can override it here.'
                            : 'Your commission rate on this deal — set by an admin.'
                      }
                    />
                  </FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      min={0}
                      max={100}
                      step="0.01"
                      placeholder={commissionEditable && !noCommission ? 'e.g. 10' : '—'}
                      disabled={!commissionEditable || noCommission}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="commission_basis"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="flex items-center gap-1.5">
                    Commission basis
                    <InfoHint text="What the commission is paid on: the client’s first paid invoice (new clients only), every paid invoice on this deal, or nothing at all." />
                  </FormLabel>
                  <Select
                    value={field.value}
                    onValueChange={(value) => {
                      field.onChange(value);
                      if (value === 'none') {
                        form.setValue('commission_pct', '', { shouldValidate: true });
                      }
                    }}
                    disabled={!commissionEditable}
                  >
                    <FormControl>
                      <SelectTrigger className="w-full">
                        <SelectValue />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {COMMISSION_BASES.map((b) => (
                        <SelectItem key={b.value} value={b.value}>
                          {b.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </FormSection>

        <FormSection variant="card" title="Pipeline" description="Where in the funnel?">
          <div className="grid gap-4 sm:grid-cols-2">
            <FormField
              control={form.control}
              name="stage"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Stage</FormLabel>
                  <Select value={field.value} onValueChange={field.onChange}>
                    <FormControl>
                      <SelectTrigger className="w-full">
                        <SelectValue />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {DEAL_STAGES.map((s) => (
                        <SelectItem key={s.value} value={s.value}>
                          {s.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="probability"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Probability %</FormLabel>
                  <FormControl>
                    <Input type="number" min={0} max={100} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          {isLost ? (
            <div className="grid gap-4 sm:grid-cols-2">
              <FormField
                control={form.control}
                name="lost_reason"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Lost reason</FormLabel>
                    <FormControl>
                      <LostReasonSelect
                        value={field.value as LostReason | ''}
                        onChange={field.onChange}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="lost_note"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Lost note</FormLabel>
                    <FormControl>
                      <Input placeholder="Who they chose, what the gap was…" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          ) : null}

          <FormField
            control={form.control}
            name="expected_close_date"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Expected close date</FormLabel>
                <FormControl>
                  <Input type="date" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </FormSection>

        <FormSection variant="card" title="Relations" description="Who's involved?">
          <div className="grid gap-4 sm:grid-cols-2">
            <FormField
              control={form.control}
              name="company"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Company</FormLabel>
                  <FormControl>
                    <EntitySelect
                      queryKey="company-picker"
                      value={field.value}
                      onChange={field.onChange}
                      search={searchCompanies}
                      resolveLabel={resolveCompanyLabel}
                      placeholder="Select a company"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="primary_contact"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Primary contact</FormLabel>
                  <FormControl>
                    <EntitySelect
                      queryKey="contact-picker"
                      value={field.value}
                      onChange={field.onChange}
                      search={searchContacts}
                      resolveLabel={resolveContactLabel}
                      placeholder="No contact"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <FormField
              control={form.control}
              name="owner"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="flex items-center gap-1.5">
                    Owner
                    <InfoHint text="The sales person responsible for this deal." />
                  </FormLabel>
                  <FormControl>
                    <MemberSelect
                      queryKey="deal-owner-picker"
                      value={field.value}
                      onChange={field.onChange}
                      placeholder="Assign an owner"
                      roles={CRM_OWNER_ROLES}
                      clearable
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="lead"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Lead</FormLabel>
                  <FormControl>
                    <EntitySelect
                      queryKey="lead-picker"
                      value={field.value}
                      onChange={field.onChange}
                      search={searchLeads}
                      resolveLabel={resolveLeadLabel}
                      placeholder="No source lead"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </FormSection>

        {form.formState.errors.root ? (
          <p className="text-destructive text-sm">{form.formState.errors.root.message}</p>
        ) : null}

        <div className="flex gap-2">
          <Button type="submit" disabled={submitting}>
            {submitting ? 'Saving…' : submitLabel}
          </Button>
          <Button type="button" variant="outline" onClick={onCancel}>
            Cancel
          </Button>
        </div>
      </form>
    </Form>
  );
}
